/**
 * Axioma W1 NB-IoT Water Meter Status Byte Definitions
 * Based on Axioma Metering UAB "Status byte description" (Kaunas 2023)
 *
 * Status byte structure:
 * - Bit 2 (0x04): Low battery - can combine with any other error
 * - Bit 3 (0x08): Permanent error (HW/SW error or Tamper) - can combine with any other error
 * - Bits 4-7: Temporary errors (mutually exclusive patterns, prioritized)
 *
 * Temporary errors use composite bit patterns (not individual bits):
 * - 0x10 (bit 4 only): Empty pipe
 * - 0x30 (bits 4,5): Leakage
 * - 0x70 (bits 4,5,6): Reverse flow
 * - 0x90 (bits 4,7): Freeze
 * - 0xB0 (bits 4,5,7): Burst
 *
 * Priority for temporary errors: Freeze > Leakage > Burst > Reverse flow > Empty pipe
 */

// Independent bit flags (can combine with temporary errors)
export const INDEPENDENT_ERRORS = {
  LOW_BATTERY: {
    mask: 0x04,
    label: "Low Battery",
    description: "Battery lifetime less than 12 months",
  },
  PERMANENT_ERROR: {
    mask: 0x08,
    label: "Permanent Error",
    description: "Hardware/Software error or Tamper infringement",
  },
} as const;

// Temporary error patterns (bits 4-7, mutually exclusive based on priority)
export const TEMPORARY_ERROR_PATTERNS = {
  FREEZE: {
    pattern: 0x90,
    label: "Freeze",
    description: "Water temperature below threshold (2/3/4/5°C) for 5 minutes",
    priority: 1,
  },
  LEAKAGE: {
    pattern: 0x30,
    label: "Leakage",
    description: "Constant flow rate within 24 hours exceeds threshold",
    priority: 2,
  },
  BURST: {
    pattern: 0xb0,
    label: "Burst",
    description: "Constant flow rate within 60 minutes exceeds threshold",
    priority: 3,
  },
  REVERSE_FLOW: {
    pattern: 0x70,
    label: "Reverse Flow",
    description: "Negative water flow detected for 1 minute",
    priority: 4,
  },
  EMPTY_PIPE: {
    pattern: 0x10,
    label: "Empty Pipe",
    description: "Absence of water in measurement section for ~30 seconds",
    priority: 5,
  },
} as const;

// Complete status byte lookup table from Axioma documentation
export const STATUS_BYTE_TABLE: Record<number, string[]> = {
  0x00: [],
  0x04: ["LOW_BATTERY"],
  0x08: ["PERMANENT_ERROR"],
  0x0c: ["PERMANENT_ERROR", "LOW_BATTERY"],
  0x10: ["EMPTY_PIPE"],
  0x14: ["EMPTY_PIPE", "LOW_BATTERY"],
  0x18: ["EMPTY_PIPE", "PERMANENT_ERROR"],
  0x1c: ["EMPTY_PIPE", "PERMANENT_ERROR", "LOW_BATTERY"],
  0x30: ["LEAKAGE"],
  0x34: ["LEAKAGE", "LOW_BATTERY"],
  0x38: ["LEAKAGE", "PERMANENT_ERROR"],
  0x3c: ["LEAKAGE", "PERMANENT_ERROR", "LOW_BATTERY"],
  0x70: ["REVERSE_FLOW"],
  0x74: ["REVERSE_FLOW", "LOW_BATTERY"],
  0x78: ["REVERSE_FLOW", "PERMANENT_ERROR"],
  0x7c: ["REVERSE_FLOW", "PERMANENT_ERROR", "LOW_BATTERY"],
  0x90: ["FREEZE"],
  0x94: ["FREEZE", "LOW_BATTERY"],
  0x98: ["FREEZE", "PERMANENT_ERROR"],
  0x9c: ["FREEZE", "PERMANENT_ERROR", "LOW_BATTERY"],
  0xb0: ["BURST"],
  0xb4: ["BURST", "LOW_BATTERY"],
  0xb8: ["BURST", "PERMANENT_ERROR"],
  0xbc: ["BURST", "PERMANENT_ERROR", "LOW_BATTERY"],
};

type ErrorInfo = { label: string; description: string; code?: string };

/**
 * Get error info by code
 */
function getErrorInfo(code: string): ErrorInfo | null {
  if (code === "LOW_BATTERY" || code === "PERMANENT_ERROR") {
    const info = INDEPENDENT_ERRORS[code];
    return { label: info.label, description: info.description, code };
  }
  if (code in TEMPORARY_ERROR_PATTERNS) {
    const info = TEMPORARY_ERROR_PATTERNS[code as keyof typeof TEMPORARY_ERROR_PATTERNS];
    return { label: info.label, description: info.description, code };
  }
  return null;
}

/**
 * Parse status byte and return list of active errors
 * Based on Axioma Metering UAB "Status byte description" document
 *
 * @param statusByte - The numeric status byte from the meter (0-255)
 * @param excludeReverseFlow - Whether to exclude reverse flow from results (default: false)
 */
export function parseErrorCode(
  statusByte: number,
  excludeReverseFlow: boolean = false
): ErrorInfo[] {
  // Handle 2-byte error codes by using only the low byte
  const value = statusByte & 0xff;

  if (value === 0) {
    return [{ label: "No Errors", description: "No error conditions detected" }];
  }

  const activeErrors: ErrorInfo[] = [];

  // Method 1: Try exact lookup from the documented table
  if (value in STATUS_BYTE_TABLE) {
    const codes = STATUS_BYTE_TABLE[value];
    for (const code of codes) {
      if (excludeReverseFlow && code === "REVERSE_FLOW") {
        continue;
      }
      const info = getErrorInfo(code);
      if (info) {
        activeErrors.push(info);
      }
    }
  } else {
    // Method 2: Parse by analyzing bit patterns (for undocumented combinations)
    // Check independent bits first
    if (value & INDEPENDENT_ERRORS.LOW_BATTERY.mask) {
      activeErrors.push({
        label: INDEPENDENT_ERRORS.LOW_BATTERY.label,
        description: INDEPENDENT_ERRORS.LOW_BATTERY.description,
        code: "LOW_BATTERY",
      });
    }

    if (value & INDEPENDENT_ERRORS.PERMANENT_ERROR.mask) {
      activeErrors.push({
        label: INDEPENDENT_ERRORS.PERMANENT_ERROR.label,
        description: INDEPENDENT_ERRORS.PERMANENT_ERROR.description,
        code: "PERMANENT_ERROR",
      });
    }

    // Check temporary error patterns (bits 4-7)
    const tempErrorBits = value & 0xf0;
    if (tempErrorBits !== 0) {
      // Check patterns in priority order
      const patterns = Object.entries(TEMPORARY_ERROR_PATTERNS).sort(
        (a, b) => a[1].priority - b[1].priority
      );

      for (const [code, pattern] of patterns) {
        if (tempErrorBits === pattern.pattern) {
          if (excludeReverseFlow && code === "REVERSE_FLOW") {
            break;
          }
          activeErrors.push({
            label: pattern.label,
            description: pattern.description,
            code,
          });
          break; // Only one temporary error can be active
        }
      }
    }
  }

  return activeErrors.length > 0
    ? activeErrors
    : [{ label: "No Errors", description: "No error conditions detected" }];
}

/**
 * Get a short summary of the status byte
 * @param statusByte - The numeric status byte from the meter
 * @param excludeReverseFlow - Whether to exclude reverse flow from summary (default: false)
 */
export function getErrorCodeSummary(
  statusByte: number,
  excludeReverseFlow: boolean = false
): string {
  const errors = parseErrorCode(statusByte, excludeReverseFlow);
  if (errors.length === 1 && errors[0].label === "No Errors") {
    return "No Errors";
  }
  return errors.map((e) => e.label).join(", ");
}

/**
 * Decode a status byte value and return detailed information
 * Useful for debugging and displaying alarm details
 * @param value - The status byte (0-255)
 */
export function decodeStatusByte(value: number) {
  const errors = parseErrorCode(value, false);
  const binary = value.toString(2).padStart(8, "0");

  const hasError = (code: string) => errors.some((e) => e.code === code);

  return {
    value,
    hex: "0x" + value.toString(16).toUpperCase().padStart(2, "0"),
    binary,
    errors,
    hasAlarms: errors.length > 0 && errors[0].label !== "No Errors",
    // Convenience flags
    hasEmptyPipe: hasError("EMPTY_PIPE"),
    hasReverseFlow: hasError("REVERSE_FLOW"),
    hasBurst: hasError("BURST"),
    hasFreeze: hasError("FREEZE"),
    hasLeakage: hasError("LEAKAGE"),
    hasLowBattery: hasError("LOW_BATTERY"),
    hasPermanentError: hasError("PERMANENT_ERROR"),
    summary: getErrorCodeSummary(value),
  };
}

// Backward compatibility - keep old export names but with updated logic
export const ERROR_CODE_DEFINITIONS = {
  0x04: INDEPENDENT_ERRORS.LOW_BATTERY,
  0x08: INDEPENDENT_ERRORS.PERMANENT_ERROR,
  0x10: TEMPORARY_ERROR_PATTERNS.EMPTY_PIPE,
  0x30: TEMPORARY_ERROR_PATTERNS.LEAKAGE,
  0x70: TEMPORARY_ERROR_PATTERNS.REVERSE_FLOW,
  0x90: TEMPORARY_ERROR_PATTERNS.FREEZE,
  0xb0: TEMPORARY_ERROR_PATTERNS.BURST,
} as const;

// Deprecated - kept for backward compatibility
export const EXTENDED_ERROR_DEFINITIONS: Record<
  number,
  { label: string; description: string }
> = {};
